import { Show, SimpleForm, TextInput, SelectInput, NumberInput, AutocompleteInput, Create, Edit } from 'react-admin';
import { Box, Typography, Paper, Chip, Alert, Grid } from '@mui/material';
import { School, Business, Category } from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';
import { COURSE_STATUS, DELIVERY_MODE } from './constants';
import { useOfoChoices } from './hooks';

export const CourseCreate = () => {
  const [searchParams] = useSearchParams();
  const prefillProviderId = searchParams.get('providerId') || '';
  const prefillProviderName = searchParams.get('providerName') || '';
  const ofoChoices = useOfoChoices();

  return (
    <Create>
      <SimpleForm>
        <Alert severity="info" sx={{ mb: 3 }}>
          Courses must be linked to an accredited training provider. Only providers with Active accreditation status are shown.
        </Alert>

        <Typography variant="subtitle2" sx={{ mb: 1.5, color: 'primary.main', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Business fontSize="small" /> Training Provider
        </Typography>
        <TextInput source="providerId" label="Provider ID" fullWidth defaultValue={prefillProviderId} helperText={prefillProviderName ? `Provider: ${prefillProviderName}` : 'Enter provider ID or select from list'} sx={{ mb: 3 }} />
        {prefillProviderName && (
          <Chip label={`Provider: ${prefillProviderName}`} sx={{ mb: 3, mt: -2 }} />
        )}

        <Typography variant="subtitle2" sx={{ mb: 1.5, color: 'primary.main', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
          <School fontSize="small" /> Course Details
        </Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextInput source="courseTitle" label="Course Title" fullWidth />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextInput source="setaQualificationCode" label="SETA Qualification Code" fullWidth />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextInput source="unitStandardCode" label="Unit Standard Code" fullWidth />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <SelectInput source="nqfLevel" choices={Array.from({ length: 10 }, (_, i) => ({ id: String(i + 1), name: String(i + 1) }))} fullWidth label="NQF Level" defaultValue="3" />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <NumberInput source="creditValue" label="Credit Value" fullWidth />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <NumberInput source="durationHours" label="Duration (hours)" fullWidth />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <NumberInput source="durationDays" label="Duration (days)" fullWidth />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <SelectInput source="deliveryMode" choices={DELIVERY_MODE.map(d => ({ id: d, name: d }))} fullWidth label="Delivery Mode" defaultValue="Classroom" />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <SelectInput source="status" choices={COURSE_STATUS.map(s => ({ id: s, name: s }))} fullWidth defaultValue="Active" label="Course Status" />
          </Grid>
        </Grid>

        <Typography variant="subtitle2" sx={{ mb: 1.5, color: 'primary.main', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
          Description
        </Typography>
        <TextInput source="courseDescription" label="Course Description" multiline fullWidth rows={8} placeholder="Describe the course content, learning outcomes, and target audience..." sx={{ mb: 3 }} />

        <Typography variant="subtitle2" sx={{ mb: 1.5, color: 'primary.main', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Category fontSize="small" /> Linked OFO Codes
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Search and select OFO codes for skills gap analysis.
        </Typography>
        <AutocompleteInput
          source="ofocodes"
          choices={ofoChoices}
          multiple
          label="OFO Codes"
          parse={(value) => (value || []).map((v: string) => ({ id: v }))}
          format={(value) => (value || []).map((v: { id: string }) => v.id)}
          fullWidth
        />
      </SimpleForm>
    </Create>
  );
};

export const CourseEdit = () => {
  const ofoChoices = useOfoChoices();

  return (
  <Edit>
    <SimpleForm>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2, mb: 2 }}>
        <TextInput source="courseTitle" label="Course Title" fullWidth />
        <TextInput source="setaQualificationCode" label="SETA Qualification Code" fullWidth />
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2, mb: 2 }}>
        <TextInput source="unitStandardCode" label="Unit Standard Code" fullWidth />
        <SelectInput source="nqfLevel" choices={Array.from({ length: 10 }, (_, i) => ({ id: String(i + 1), name: String(i + 1) }))} fullWidth label="NQF Level" />
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2, mb: 2 }}>
        <NumberInput source="creditValue" label="Credit Value" fullWidth />
        <NumberInput source="durationHours" label="Duration (hours)" fullWidth />
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2, mb: 2 }}>
        <NumberInput source="durationDays" label="Duration (days)" fullWidth />
        <SelectInput source="deliveryMode" choices={DELIVERY_MODE.map(d => ({ id: d, name: d }))} fullWidth label="Delivery Mode" />
      </Box>
      <TextInput source="courseDescription" label="Course Description" multiline fullWidth rows={3} sx={{ mb: 2 }} />
      <AutocompleteInput source="ofocodes" choices={ofoChoices} multiple label="OFO Codes" parse={(value) => (value || []).map((v: string) => ({ id: v }))} format={(value) => (value || []).map((v: { id: string }) => v.id)} fullWidth sx={{ mb: 2 }} />
      <SelectInput source="status" choices={COURSE_STATUS.map(s => ({ id: s, name: s }))} fullWidth label="Course Status" sx={{ mb: 2 }} />
    </SimpleForm>
  </Edit>
  );
};
